import { NavigationLink } from "../NavigationLink";
import { useNavigationActiveState } from "../hooks";
import { SubMenuItemType } from "../types";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./CustomAccordion";
import { SubMenuItem } from "./SubMenuItem";
import { MENU_STYLES } from "./constants";
import { useMenuItemLogic } from "./hooks";
import { MenuItemProps } from "./types";

import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

interface AccordionTriggerProps {
  item: { name: string; icon: React.ComponentType<{ className?: string }> };
  isActive: boolean;
}

const QuickLinksAccordionTrigger = ({ item, isActive }: AccordionTriggerProps) => {
  const Icon = item.icon;

  return (
    <AccordionTrigger
      className={cn("w-full p-0", MENU_STYLES.accordion.trigger, isActive && "bg-gray-100")}
    >
      <SidebarMenuButton
        isActive={isActive}
        className={`w-full ${MENU_STYLES.menuButton.base} ${MENU_STYLES.menuButton.expanded}`}
      >
        <Icon className="h-4 w-4 transition-colors duration-200" />
        <span className="font-medium">{item.name}</span>
      </SidebarMenuButton>
    </AccordionTrigger>
  );
};

interface RegularItemAccordionTriggerProps extends AccordionTriggerProps {
  firstSubItemHref: string;
}

const RegularItemAccordionTrigger = ({
  item,
  isActive,
  firstSubItemHref,
}: RegularItemAccordionTriggerProps) => {
  const Icon = item.icon;

  return (
    <div className="group/menu flex items-center">
      <NavigationLink to={firstSubItemHref} className="flex-1">
        <SidebarMenuButton
          isActive={isActive}
          className={`${MENU_STYLES.menuButton.base} ${MENU_STYLES.menuButton.expanded}`}
        >
          <Icon className="h-4 w-4 transition-transform duration-200" />
          <span className="font-medium">{item.name}</span>
        </SidebarMenuButton>
      </NavigationLink>
      <AccordionTrigger className="px-1 py-0">
        <div className={MENU_STYLES.accordion.expandButton} />
      </AccordionTrigger>
    </div>
  );
};

interface SubMenuItemRendererProps {
  subItem: SubMenuItemType;
  parentName: string;
  isMenuOpen: (key: string) => boolean;
  toggleMenu: (key: string, hasSubItems: boolean) => void;
}

const SubMenuItemRenderer = ({
  subItem,
  parentName,
  isMenuOpen,
  toggleMenu,
}: SubMenuItemRendererProps) => {
  const { isItemActive } = useNavigationActiveState();
  const { hasSubItems, getFirstNestedItemHref, getMenuKey } = useMenuItemLogic();

  const subItemActive = isItemActive(subItem);
  const menuKey = getMenuKey(parentName, subItem.name);
  const subItemOpen = isMenuOpen(menuKey);

  // Handle clickable items
  if (subItem.onClick) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton onClick={subItem.onClick} className={MENU_STYLES.submenu.base}>
          <SubMenuItem item={subItem} textClassName={MENU_STYLES.submenu.text} />
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  // Sub-item with nested items using nested Accordion
  if (hasSubItems(subItem)) {
    const firstNestedItemHref = getFirstNestedItemHref(subItem);

    return (
      <SidebarMenuSubItem>
        <Accordion
          value={subItemOpen ? subItem.name : ""}
          onValueChange={() => {
            toggleMenu(menuKey, true);
          }}
        >
          <AccordionItem value={subItem.name}>
            <div className="group/submenu flex items-center">
              <NavigationLink to={firstNestedItemHref} className="flex-1">
                <SidebarMenuSubButton isActive={subItemActive} className={MENU_STYLES.submenu.base}>
                  <SubMenuItem
                    item={subItem}
                    textClassName={MENU_STYLES.submenu.text}
                    skipNavigation
                  />
                </SidebarMenuSubButton>
              </NavigationLink>
              <AccordionTrigger className="px-0.5 py-0">
                <div className="rounded-md p-0.5 opacity-0 transition-all duration-200 hover:bg-gray-100/80 group-hover/submenu:opacity-100" />
              </AccordionTrigger>
            </div>
            <AccordionContent className="pb-0 pt-0">
              <SidebarMenuSub className="ml-3">
                {subItem.items?.map(nestedItem => (
                  <SidebarMenuSubItem key={nestedItem.name}>
                    <NavigationLink to={nestedItem.href ?? "#"}>
                      <SidebarMenuSubButton
                        isActive={isItemActive(nestedItem)}
                        className={MENU_STYLES.submenu.nested}
                      >
                        <SubMenuItem
                          item={nestedItem}
                          textClassName={MENU_STYLES.submenu.nestedText}
                          skipNavigation
                        />
                      </SidebarMenuSubButton>
                    </NavigationLink>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </SidebarMenuSubItem>
    );
  }

  // Regular sub-item
  return (
    <SidebarMenuSubItem>
      {/* @ts-expect-error - TODO: fix this */}
      <NavigationLink to={subItem.href}>
        <SidebarMenuSubButton isActive={subItemActive} className={MENU_STYLES.submenu.base}>
          <SubMenuItem item={subItem} textClassName={MENU_STYLES.submenu.text} skipNavigation />
        </SidebarMenuSubButton>
      </NavigationLink>
    </SidebarMenuSubItem>
  );
};

export const ExpandedMenuItem = ({
  item,
  isActive,
  isOpen,
  isMenuOpen,
  toggleMenu,
}: MenuItemProps) => {
  const { isQuickLinks, getFirstItemHref } = useMenuItemLogic();

  const isQuickLinksItem = isQuickLinks(item);
  const firstSubItemHref = getFirstItemHref(item);

  return (
    <SidebarMenuItem>
      <Accordion
        value={isOpen ? item.name : ""}
        onValueChange={() => {
          toggleMenu(item.name, true);
        }}
      >
        <AccordionItem value={item.name}>
          {isQuickLinksItem ? (
            <QuickLinksAccordionTrigger item={item} isActive={isActive} />
          ) : (
            <RegularItemAccordionTrigger
              item={item}
              isActive={isActive}
              firstSubItemHref={firstSubItemHref}
            />
          )}
          <AccordionContent className="pb-0 pt-0">
            <SidebarMenuSub className="ml-3.5 mt-1">
              {item.items?.map(subItem => (
                <SubMenuItemRenderer
                  key={subItem.name}
                  subItem={subItem}
                  parentName={item.name}
                  isMenuOpen={isMenuOpen}
                  toggleMenu={toggleMenu}
                />
              ))}
            </SidebarMenuSub>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </SidebarMenuItem>
  );
};
